import assert from 'assert';
import moment from 'moment';
import typeCheck from 'common/util/typeCheck';
import { formatPrice } from 'common/formatters';
import isValidDateWithoutTimeString from 'common/util/isValidDateWithoutTimeString';

import {
  CONCESSION_TYPE_RENT_FREE,
  CONCESSION_TYPE_RENT_DISCOUNT,
  CONCESSION_TYPE_CUSTOM,
  CONCESSION_TYPES,
  CONCESSION_SCHEDULE_TYPES,
  CONCESSION_DESCRIPTION_VARIATION_BANNER,
  CONCESSION_DESCRIPTION_VARIATION_RENTLINX,
  CONCESSION_STATUS_NOT_STARTED,
  CONCESSION_STATUS_ACTIVE,
  CONCESSION_STATUS_ENDING_SOON,
  CONCESSION_STATUS_EXPIRED,
  CONCESSION_SCHEDULE_TYPE_SECOND_MONTH,
  CONCESSION_DESCRIPTION_VARIATION_HOUSEHOLD_NOTE,
  CONCESSION_DESCRIPTION_VARIATION_PARTNER_APPROVAL,
  CONCESSION_DESCRIPTION_VARIATION_LEASE_DRAFT,
} from 'common/concessionConstants';

export const getConcessionStartMoment = ({ concession }) => {
  const concessionStartMoment = moment(concession.startDate)
    .startOf('day');

  return concessionStartMoment;
};

export const getConcessionEndMoment = ({ concession }) => {
  const concessionEndMoment = moment(concession.endDate)
    .endOf('day');

  return concessionEndMoment;
};

export const isConcessionActive = (concession) => {
  return moment().isBetween(
    getConcessionStartMoment({ concession }),
    getConcessionEndMoment({ concession }),
  );
};

export const hasActiveConcession = (concessions) => {
  if (!Array.isArray(concessions)) {
    return false;
  }
  return concessions.filter((concession) => isConcessionActive(concession)).length > 0;
};

export const getLatestActiveConcession = (concessions) => {
  if (!Array.isArray(concessions)) {
    return false;
  }

  const activeConcessions = concessions.filter((c) => isConcessionActive(c))
    .sort((a, b) => moment(a.startDate).isAfter(moment(b.startDate) ? 1 : -1));

  return activeConcessions.length > 0 ? activeConcessions[0] : null;
};

export const getConcessionHoursDiff = ({ concession }) => {
  const nowMoment = moment();
  const endDateMoment = getConcessionEndMoment({ concession });
  const hoursDiff = endDateMoment.diff(nowMoment, 'hours');

  return hoursDiff;
};

export const getConcessionStatus = ({ concession }) => {
  if (moment().isBefore(getConcessionStartMoment({ concession }))) {
    return CONCESSION_STATUS_NOT_STARTED;
  }
  const hoursDiff = getConcessionHoursDiff({ concession });
  if (hoursDiff <= 0) {
    return CONCESSION_STATUS_EXPIRED;
  }
  if (hoursDiff <= 120) {
    return CONCESSION_STATUS_ENDING_SOON;
  }
  return CONCESSION_STATUS_ACTIVE;
};

export const getConcessionExpirationText = ({ concession }) => {
  const expirationMoment = getConcessionEndMoment({ concession })
    .format('MMMM Do');

  return `Ends ${expirationMoment}`;
};

const getRentDiscountProratedNERFields = ({ concession, rentPrice, leaseTermInMonths = 12 }) => {
  const proratedOverMonths = (leaseTermInMonths - 1);
  const rentReduction = concession.amount / proratedOverMonths; // Check if proratedTimeframeInMonths should be 11
  const rentAfterFirstMonth = rentPrice - rentReduction;

  return {
    rentAfterFirstMonth,
    proratedOverMonths,
  };
};

export const calculateNetEffectiveRent = ({ concession, rentPrice, leaseTermInMonths = 12 }) => {
  typeCheck('concession::NonEmptyObject', concession);
  typeCheck('rentPrice::PositiveNumber', rentPrice);
  typeCheck('leaseTermInMonths::PositiveNumber', leaseTermInMonths);

  const grossRent = (rentPrice * leaseTermInMonths) - concession.amount;
  const netEffectiveRent = Math.round(grossRent / leaseTermInMonths);

  const defaultNERFields = {
    concessionAmount: concession.amount,
    firstMonthRent: rentPrice,
    grossRent,
    netEffectiveRent,
    rentAfterFirstMonth: null,
    proratedOverMonths: null,
    secondMonthRent: null,
  };

  switch (concession.type) {
    case CONCESSION_TYPE_RENT_DISCOUNT:
      if (concession.scheduleType === CONCESSION_SCHEDULE_TYPE_SECOND_MONTH) {
        return {
          ...defaultNERFields,
          secondMonthRent: rentPrice - concession.amount,
        };
      }
      return {
        ...defaultNERFields,
        ...getRentDiscountProratedNERFields({ concession, rentPrice, leaseTermInMonths }),
      };
    default:
      return defaultNERFields;
  }
};

export const transformNetEffectiveRentToTextList = (netEffectiveRent) => {
  if (!netEffectiveRent) {
    return null;
  }

  const {
    concessionAmount,
    firstMonthRent,
    rentAfterFirstMonth,
    proratedOverMonths,
  } = netEffectiveRent;

  return [
    `${formatPrice(netEffectiveRent.netEffectiveRent)} = Net Effective Rent`,
    `${formatPrice(rentAfterFirstMonth)} = Discounted Monthly Rent. ` +
    `(${formatPrice(concessionAmount)} Concession applied, prorated over ${proratedOverMonths} months.)`,
    `${formatPrice(firstMonthRent)} = First Month Rent (Full rent rate)`,
  ];
};

export const transformConcessionToTextList = ({
  concession,
  rentPrice,
  leaseTermInMonths = 12,
  variation = CONCESSION_DESCRIPTION_VARIATION_BANNER,
}) => {
  typeCheck('concession::NonEmptyObject', concession);
  typeCheck('rentPrice::PositiveNumber', rentPrice);

  const endMoment = getConcessionEndMoment({ concession });
  const formattedEndingDate = endMoment.format('MMM Do');
  const { rentAfterFirstMonth } = calculateNetEffectiveRent({ concession, rentPrice, leaseTermInMonths });

  switch (variation) {
    case CONCESSION_DESCRIPTION_VARIATION_BANNER:
      if (concession.scheduleType === CONCESSION_SCHEDULE_TYPE_SECOND_MONTH) {
        return [
          'This discount is already reflected in the advertised rent price.',
          `${formatPrice(concession.amount)} taken off rent the second month`,
          `Move in by ${formattedEndingDate} to receive special!`,
        ];
      }
      return [
        `Move in before ${formattedEndingDate} to receive special.`,
        'Discounted rent starting the second month ' +
        `= ${formatPrice(rentAfterFirstMonth)} (assumes 12 month lease).`,
        `First month rent ${formatPrice(rentPrice)}.`,
      ];

    case CONCESSION_DESCRIPTION_VARIATION_RENTLINX:
      if (concession.scheduleType === CONCESSION_SCHEDULE_TYPE_SECOND_MONTH) {
        return [
          `${formatPrice(concession.amount)} Rent Special!!`,
          `Move in before ${formattedEndingDate} to get ${formatPrice(concession.amount)} off rent ` +
          'in the second month. Apply now to secure this discount!',
        ];
      }
      return [
        `${formatPrice(concession.amount)} Rent Special!!`,
        `Move in before ${formattedEndingDate} to receive this rent discount.`,
        `Your discounted rent will be ${formatPrice(rentAfterFirstMonth)}, ` +
        'starting the second month (assuming a 12 month lease).',
        `The first month’s rent will be ${formatPrice(rentPrice)}.`,
        `This special expires on ${formattedEndingDate}. Apply now to secure this discount!`,
      ];

    case CONCESSION_DESCRIPTION_VARIATION_HOUSEHOLD_NOTE:
      if (concession.scheduleType === CONCESSION_SCHEDULE_TYPE_SECOND_MONTH) {
        return [
          `Second month of rent is reduced by ${formatPrice(concession.amount)}.`,
          `This only applies if the renter moves in by ${formattedEndingDate}.`,
        ];
      }
      return [
        `The discounted rent will be ${formatPrice(rentAfterFirstMonth)}, ` +
          'starting the second month (assuming a 12 month lease).',
        `The first month’s rent will be ${formatPrice(rentPrice)}.`,
        `This only applies if the renter moves in by ${formattedEndingDate}.`,
      ];

    case CONCESSION_DESCRIPTION_VARIATION_PARTNER_APPROVAL:
      if (concession.scheduleType === CONCESSION_SCHEDULE_TYPE_SECOND_MONTH) {
        return [
          `${formatPrice(concession.amount)} concession applies to second leasing month.`,
          'First month paid in full.',
          `Renter must move in by ${formattedEndingDate}.`,
        ];
      }
      return [
        `${formatPrice(concession.amount)} concession is prorated.`,
        `The discounted rent will be ${formatPrice(rentAfterFirstMonth)}, ` +
        'starting the second month (assuming a 12 month lease).',
        `The first month’s rent is paid in full: ${formatPrice(rentPrice)}.`,
        `This only applies if the renter moves in by ${formattedEndingDate}.`,
      ];

    case CONCESSION_DESCRIPTION_VARIATION_LEASE_DRAFT:
      if (concession.scheduleType === CONCESSION_SCHEDULE_TYPE_SECOND_MONTH) {
        return [
          `${formatPrice(concession.amount)} concession applies to second leasing month. `,
          'First month paid in full.',
          `Only apply this concession if the renter has a move-in date on or before ${formattedEndingDate}.`,
        ];
      }
      return [
        `${formatPrice(concession.amount)} concession is prorated.`,
        `The discounted rent will be ${formatPrice(rentAfterFirstMonth)}, ` +
        'starting the second month (assuming a 12 month lease).',
        'The first month’s rent is paid in full',
        `Only apply this concession if the renter has a move-in date on or before ${formattedEndingDate}.`,
      ];
    default:
      break;
  }
};

/**
 * Checks if a concession is a valid concession. Throws an error if not.
 *
 * @return {boolean} `true` if the concession is valid.
 */
export const assertValidConcession = (concession) => {
  typeCheck('params::NonEmptyObject', concession);

  const {
    startDate,
    endDate,
    type,
    amount,
    customHeadline,
    customDescription,
    termsAndConditions,
    rentFreeWeeks,
    scheduleType,
    listingId,
    createdByUserId,
  } = concession;

  typeCheck('startDate::Maybe DateString', startDate);
  assert(
    !startDate || isValidDateWithoutTimeString(startDate),
    `"${startDate}" is not a valid date string. Accepts: YYYY-MM-DD`,
  );
  typeCheck('endDate::DateString', endDate);
  assert(
    isValidDateWithoutTimeString(endDate),
    `"${endDate}" is not a valid date string. Accepts: YYYY-MM-DD`,
  );
  typeCheck('type::NonEmptyString', type);
  assert(
    CONCESSION_TYPES.includes(type),
    `Concession type "${type}" is not supported.`,
  );
  typeCheck('listingId::PositiveNumber', listingId);
  typeCheck('createdByUserId::Maybe PositiveNumber', createdByUserId);

  switch (type) {
    case CONCESSION_TYPE_RENT_FREE: {
      typeCheck('rentFreeWeeks::PositiveNumber', rentFreeWeeks);
      break;
    }
    case CONCESSION_TYPE_RENT_DISCOUNT: {
      typeCheck('amount::PositiveNumber', amount);
      typeCheck('scheduleType::NonEmptyString', scheduleType);
      assert(
        CONCESSION_SCHEDULE_TYPES.includes(scheduleType),
        `Concession schedule type "${scheduleType}" not supported.`,
      );
      break;
    }
    case CONCESSION_TYPE_CUSTOM: {
      typeCheck('amount::PositiveNumber', amount);
      typeCheck('customHeadline::NonEmptyString', customHeadline);
      typeCheck('customDescription::NonEmptyString', customDescription);
      typeCheck('termsAndConditions::NonEmptyString', termsAndConditions);
      break;
    }
  }

  if (moment(endDate).isBefore(moment(startDate))) {
    const error = new Error('End date can not be earlier than the start date.');
    error.name = 'SrConcessionEndDateBeforeStartDateError';
    error.details = {
      ...concession,
    };
    throw error;
  }

  return true;
};
